import {
    Container,
    Form,
    InputGroup,
    FloatingLabel,
    Button,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import useUserApi from '@/shared/services/userApi';
import logger from '@/shared/utils/logger';
import userIcon from '@/assets/img/icons/svg/userIcon.svg';

import './CreateProfile.css';

const usernameRegex = /^[a-z0-9_-]{4,25}$/;

const SetupUsername = () => {
    const [username, setUsername] = useState('');
    const [touched, setTouched] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { refreshProfileStatus } = useAuth();
    const { setupUsername } = useUserApi();
    const navigate = useNavigate();

    const validate = (val) => {
        if (!val.trim()) return 'Username is required';
        if (!usernameRegex.test(val.trim())) {
            return 'Username must be 4–25 characters: lowercase letters, numbers, _ or - only';
        }
        return '';
    };

    const handleChange = (e) => {
        const val = e.target.value.toLowerCase();
        setUsername(val);
        setError(validate(val));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setTouched(true);
        const validationError = validate(username);
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        try {
            await setupUsername(username.trim());
            refreshProfileStatus();
            navigate('/dashboard');
        } catch (err) {
            if (err.response?.status === 409) {
                setError(
                    'That username is already taken. Please choose another.'
                );
            } else {
                setError('Something went wrong. Please try again.');
                logger.error('setupUsername failed:', err);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div id="create-profile" className="bg-primary">
            <Container className="m-auto py-5">
                <Form
                    noValidate
                    onSubmit={handleSubmit}
                    className="create-profile-form d-flex flex-column align-items-center bg-white gap-3 mx-auto w-100 py-5 px-5"
                >
                    <h1 className="h1 text-primary text-center mb-1">
                        Choose Your Username
                    </h1>
                    <p className="text-muted text-center mb-3">
                        Your account was created before usernames were required.
                        Please pick a username to continue.
                    </p>

                    <Form.Group className="w-100 mb-3">
                        <InputGroup hasValidation className="input-logo-label">
                            <img src={userIcon} alt="" />
                            <FloatingLabel
                                controlId="floatingUsername"
                                label="Username *"
                            >
                                <Form.Control
                                    type="text"
                                    placeholder="Choose a username"
                                    value={username}
                                    onChange={handleChange}
                                    onBlur={() => setTouched(true)}
                                    isInvalid={touched && !!error}
                                    isValid={
                                        touched &&
                                        !error &&
                                        username.trim() !== ''
                                    }
                                    disabled={loading}
                                    aria-describedby={
                                        touched && error
                                            ? 'username-error username-help'
                                            : 'username-help'
                                    }
                                />
                            </FloatingLabel>
                        </InputGroup>
                        {touched && error && (
                            <div
                                id="username-error"
                                role="alert"
                                className="invalid-feedback d-block fw-semibold ps-3"
                            >
                                {error}
                            </div>
                        )}
                        <Form.Text
                            id="username-help"
                            className="text-muted ps-3"
                        >
                            4–25 characters. Letters, numbers, underscores, and
                            hyphens only.
                        </Form.Text>
                    </Form.Group>

                    <Button
                        className="signup-btn btn-lg bg-primary text-white text-center fw-bold m-auto py-3"
                        type="submit"
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Save Username'}
                    </Button>
                </Form>
            </Container>
        </div>
    );
};

export default SetupUsername;
